import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useTag } from '@react/hooks';
import { Button, Card, CardContent, ErrorAlert, Input } from '@react/components';
import { ColorPicker } from '@/components/ColorPicker';

const COLORS = ['#0ea5a4', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#eab308', '#22c55e', '#64748b'];

export const TagEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTag();
  const { t } = useI18n();
  const item = store.currentItem;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [icon, setIcon] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) void store.GetTag(id).catch(() => {});
    return () => store.clearCurrent();
  }, [id, store]);

  useEffect(() => {
    if (!item) return;
    setName(item.name);
    setDescription(item.description || '');
    setColor(item.color || COLORS[0]);
    setIcon(item.icon || '');
  }, [item]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id || !item || !name.trim()) return;
    setIsSubmitting(true);
    try {
      await store.UpdateTag(id, {
        tag: {
          ...item,
          entity: { ...item.entity!, id },
          name: name.trim(),
          description: description.trim() || undefined,
          color,
          icon: icon.trim() || undefined,
        },
      });
      navigate(`/tags/${id}`);
    } catch {
      // Store exposes the server error in ErrorAlert.
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading) return <p className="text-muted-foreground">{t('common.loading')}</p>;
  if (store.error && !item) return <ErrorAlert error={store.error} />;
  if (!item) return <p className="text-muted-foreground">{t('common.notFound')}</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <Link to="/tags" className="text-sm text-muted-foreground hover:text-foreground">{t('tag.backToTags')}</Link>
        <h1 className="mt-3 text-xl font-semibold text-foreground">{t('tag.edit')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('tag.editDescription')}</p>
      </div>
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input label={t('tag.name')} value={name} onChange={event => setName(event.target.value)} maxLength={100} required />
            <Input label={t('tag.description')} value={description} onChange={event => setDescription(event.target.value)} />
            <div>
              <span className="mb-2 block text-sm font-medium text-foreground">{t('tag.color')}</span>
              <ColorPicker options={COLORS.map(value => ({ value, label: value }))} value={color} onChange={setColor} />
            </div>
            <Input label={t('tag.iconOptional')} value={icon} onChange={event => setIcon(event.target.value)} maxLength={16} />
            <p className="text-sm text-muted-foreground">{t('tag.scopeImmutable')}</p>
            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <Link to={`/tags/${id}`}><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
              <Button type="submit" disabled={isSubmitting}>{isSubmitting ? t('common.saving') : t('common.saveChanges')}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
});

export default TagEditPage;
